import { FC, useMemo } from 'react'
import type { Country } from 'react-phone-number-input'
import { PhoneNumberInput } from 'src/components/phone-number-input'

type ValidatorRef = { validationType: string; value: Record<string, unknown> }

interface PhoneInputFieldProps {
  value?: string | null
  disabled?: boolean
  handleResponse: (value: string | null | undefined) => void
  // Props for interface consistency with other field types
  variant?: string
  handleSearch?: (value: string) => void
  options?: unknown[]
  loading?: boolean
  isMultiple?: boolean
  numPad?: boolean
  questionDisplay?: string | null
  layout?: string | null
  valueRef?: unknown
  validatorsRef?: ValidatorRef[]
  patternError?: string | null
  onPatternError?: (error: string | null) => void
  questionId?: string
  isSearchable?: boolean
  translate?: string | null
}

const PhoneInputField: FC<PhoneInputFieldProps> = ({
  handleResponse,
  value,
  disabled = false,
  validatorsRef,
  patternError,
}) => {
  const phoneConfig = useMemo(() => {
    const phoneValidator = validatorsRef?.find((v) => v.validationType === 'PHONE')
    if (!phoneValidator?.value) {
      return { defaultCountry: 'CO' as Country, countries: undefined }
    }

    const val = phoneValidator.value
    const countries = val.countries as string[] | undefined

    if (countries && Array.isArray(countries)) {
      return { defaultCountry: null, countries: countries as Country[] }
    }

    const country = val.country as string | undefined
    if (country === 'ANY') {
      return { defaultCountry: null, countries: undefined }
    }
    if (country) {
      return { defaultCountry: country as Country, countries: undefined }
    }

    return { defaultCountry: 'CO' as Country, countries: undefined }
  }, [validatorsRef])

  // Validation and branching are driven by the server; this field only renders
  // the input and any server-provided error passed via `patternError`.
  const handleChange = (phoneValue: string | undefined) => {
    handleResponse(phoneValue ?? null)
  }

  return (
    <div>
      <PhoneNumberInput
        defaultCountry={phoneConfig.defaultCountry}
        countries={phoneConfig.countries}
        value={value ?? undefined}
        onChange={handleChange}
        disabled={disabled}
        data-testid="survey-phone-input"
      />
      {patternError && <p className="mt-1 text-sm text-error">{patternError}</p>}
    </div>
  )
}

export default PhoneInputField
